<template>
  <div class="st-page">
    <Navigation />
    <div class="container" style="padding-top:20px;max-width:1300px;">
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="st-page-title"><i class="mdi mdi-chart-bar me-2"></i>Sales Report</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="btn st-btn-outline btn-sm" @click="setRange('today')">Today</button>
          <button class="btn st-btn-outline btn-sm" @click="setRange('week')">7d</button>
          <button class="btn st-btn-outline btn-sm" @click="setRange('mtd')">MTD</button>
          <button class="btn st-btn-outline btn-sm" @click="setRange('30d')">30d</button>
          <button class="btn st-btn-outline btn-sm" @click="exportCsv" :disabled="!report">📥 CSV</button>
          <button class="btn st-btn-primary btn-sm" @click="loadReport" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>🔄
          </button>
        </div>
      </div>

      <!-- Date range -->
      <div class="st-section mb-3" style="padding:14px;">
        <div class="d-flex gap-2 flex-wrap align-items-end">
          <div>
            <label class="st-label" style="font-size:10px;">From</label>
            <input type="date" class="st-input" v-model="dateFrom" style="min-width:150px;" />
          </div>
          <div>
            <label class="st-label" style="font-size:10px;">To</label>
            <input type="date" class="st-input" v-model="dateTo" style="min-width:150px;" />
          </div>
          <button class="btn st-btn-primary btn-sm" @click="loadReport" :disabled="loading">Apply</button>
          <div class="ms-auto" style="font-size:11px;color:#8BA4C4;align-self:center;" v-if="report">
            {{ report.date_from }} → {{ report.date_to }}
          </div>
        </div>
      </div>

      <!-- Summary cards -->
      <div class="pm-grid" v-if="report">
        <div class="pm-card" style="border-left-color:#10B981;">
          <div class="pm-card-label">Collected</div>
          <div class="pm-card-value" style="color:#10B981;">KSh {{ fmt(report.collections.total) }}</div>
          <div class="pm-card-sub">{{ report.collections.cnt }} payments</div>
        </div>
        <div class="pm-card" style="border-left-color:#4FC3F7;">
          <div class="pm-card-label">Remitted</div>
          <div class="pm-card-value" style="color:#4FC3F7;">KSh {{ fmt(report.remittance.total) }}</div>
          <div class="pm-card-sub">{{ report.remittance.cnt }} B2C entries</div>
        </div>
        <div class="pm-card" style="border-left-color:#F59E0B;">
          <div class="pm-card-label">Till Expenses</div>
          <div class="pm-card-value" style="color:#F59E0B;">KSh {{ fmt(report.expense.total) }}</div>
          <div class="pm-card-sub">{{ report.expense.cnt }} payouts</div>
        </div>
        <div class="pm-card" :style="{borderLeftColor: report.float >= 0 ? '#10B981' : '#EF4444'}">
          <div class="pm-card-label">Float in Till</div>
          <div class="pm-card-value" :style="{color: report.float >= 0 ? '#10B981' : '#EF4444'}">KSh {{ fmt(report.float) }}</div>
          <div class="pm-card-sub">collected − outflows</div>
        </div>
      </div>

      <!-- Reconciliation breakdown -->
      <div class="st-section mb-3" v-if="report" style="padding:14px;">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span style="font-size:13px;font-weight:600;color:#4FC3F7;">Reconciliation</span>
          <span style="font-size:11px;color:#8BA4C4;">All amounts in KES</span>
        </div>
        <div style="font-size:13px;">
          <div class="recon-row"><span>Collections (in)</span><strong style="color:#10B981;">+ {{ fmt(report.collections.total) }}</strong></div>
          <div class="recon-row"><span style="padding-left:14px;color:#8BA4C4;font-size:12px;">Charges (M-Pesa)</span><span style="color:#8BA4C4;font-size:12px;">− {{ fmt(report.collections.charges) }}</span></div>
          <div class="recon-row"><span style="padding-left:14px;color:#8BA4C4;font-size:12px;">Net to till</span><span style="color:#8BA4C4;font-size:12px;">= {{ fmt(report.collections.net) }}</span></div>
          <div class="recon-row"><span>Remittances (B2C out)</span><strong style="color:#4FC3F7;">− {{ fmt(report.remittance.total) }}</strong></div>
          <div class="recon-row"><span>Direct till expenses</span><strong style="color:#F59E0B;">− {{ fmt(report.expense.total) }}</strong></div>
          <div class="recon-row recon-total"><span>Float remaining</span><strong :style="{color: report.float >= 0 ? '#10B981' : '#EF4444'}">{{ fmt(report.float) }}</strong></div>
        </div>
      </div>

      <!-- Collections by method -->
      <div class="st-section mb-3" v-if="report && report.by_method.length" style="padding:14px;">
        <div style="font-size:13px;font-weight:600;color:#4FC3F7;margin-bottom:8px;">Collections by Method</div>
        <table class="pm-table">
          <thead><tr><th>Method</th><th class="text-end">Count</th><th class="text-end">Total (KES)</th></tr></thead>
          <tbody>
            <tr v-for="m in report.by_method" :key="m.payment_method">
              <td>{{ methodLabel(m.payment_method) }}</td>
              <td class="text-end">{{ m.cnt }}</td>
              <td class="text-end" style="color:#10B981;font-weight:600;">{{ fmt(m.total) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- POS Analytics -->
      <div v-if="report && report.pos" class="st-section mb-3" style="padding:14px;">
        <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <span style="font-size:13px;font-weight:600;color:#4FC3F7;"><i class="mdi mdi-point-of-sale me-1"></i>POS Analytics</span>
          <div class="d-flex gap-2 flex-wrap">
            <button class="btn btn-sm" :class="posTab === 'items' ? 'st-btn-primary' : 'st-btn-outline'" @click="posTab = 'items'">🍽 Items</button>
            <button class="btn btn-sm" :class="posTab === 'time' ? 'st-btn-primary' : 'st-btn-outline'" @click="posTab = 'time'">⏰ Time</button>
            <button class="btn btn-sm" :class="posTab === 'cashier' ? 'st-btn-primary' : 'st-btn-outline'" @click="posTab = 'cashier'">👤 Cashiers</button>
          </div>
        </div>

        <!-- POS Headline KPIs -->
        <div class="pm-grid" style="margin-bottom:14px;">
          <div class="pm-card" style="border-left-color:#10B981;">
            <div class="pm-card-label">POS Sales</div>
            <div class="pm-card-value" style="color:#10B981;">KSh {{ fmt(report.pos.total) }}</div>
            <div class="pm-card-sub">{{ report.pos.cnt }} transactions</div>
          </div>
          <div class="pm-card" style="border-left-color:#4FC3F7;">
            <div class="pm-card-label">Avg Ticket</div>
            <div class="pm-card-value" style="color:#4FC3F7;">KSh {{ fmt(report.pos.avg_ticket) }}</div>
            <div class="pm-card-sub">per transaction</div>
          </div>
          <div class="pm-card" style="border-left-color:#F59E0B;">
            <div class="pm-card-label">Unique Items</div>
            <div class="pm-card-value" style="color:#F59E0B;">{{ report.pos.unique_items }}</div>
            <div class="pm-card-sub">distinct SKUs sold</div>
          </div>
          <div class="pm-card" style="border-left-color:#A855F7;">
            <div class="pm-card-label">Best Hour</div>
            <div class="pm-card-value" style="color:#A855F7;">{{ bestHour.label }}</div>
            <div class="pm-card-sub">KSh {{ fmt(bestHour.total) }}</div>
          </div>
        </div>

        <!-- Payment Mode Mix -->
        <div v-if="report.pos.by_mode.length" class="mb-3">
          <div style="font-size:11px;color:#4A6A8A;text-transform:uppercase;font-weight:600;margin-bottom:6px;">Payment Mode Mix</div>
          <div class="mode-bar">
            <div v-for="m in report.pos.by_mode" :key="m.payment_mode"
                 :style="{width: pct(m.total, report.pos.total) + '%', background: modeColor(m.payment_mode)}"
                 :title="`${modeLabel(m.payment_mode)}: KSh ${fmt(m.total)} (${pct(m.total, report.pos.total)}%)`"
                 class="mode-bar-seg">
              <span v-if="pct(m.total, report.pos.total) > 8">{{ modeLabel(m.payment_mode) }} {{ pct(m.total, report.pos.total) }}%</span>
            </div>
          </div>
          <div class="d-flex gap-3 flex-wrap mt-2" style="font-size:11px;">
            <div v-for="m in report.pos.by_mode" :key="'lg-' + m.payment_mode" class="d-flex align-items-center gap-1">
              <span :style="{width:'10px',height:'10px',display:'inline-block',background:modeColor(m.payment_mode),borderRadius:'2px'}"></span>
              <span style="color:#E8EDF4;">{{ modeLabel(m.payment_mode) }}</span>
              <span style="color:#8BA4C4;">{{ m.cnt }} · KSh {{ fmt(m.total) }}</span>
            </div>
          </div>
        </div>

        <!-- ITEMS TAB -->
        <div v-if="posTab === 'items'">
          <div class="d-flex gap-2 mb-2 align-items-center flex-wrap">
            <button class="btn btn-sm" :class="itemSort === 'revenue' ? 'st-btn-primary' : 'st-btn-outline'" @click="itemSort = 'revenue'">By Revenue</button>
            <button class="btn btn-sm" :class="itemSort === 'qty' ? 'st-btn-primary' : 'st-btn-outline'" @click="itemSort = 'qty'">By Quantity</button>
            <div class="d-flex gap-1 align-items-center" style="margin-left:auto;">
              <select class="st-input" v-model="itemMinQty" style="max-width:130px;font-size:12px;padding:6px 8px;">
                <option :value="0">All items</option>
                <option :value="5">≥ 5 sold</option>
                <option :value="10">≥ 10 sold</option>
                <option :value="20">≥ 20 sold</option>
                <option :value="50">≥ 50 sold</option>
              </select>
              <input class="st-input" style="max-width:180px;" v-model="itemFilter" placeholder="🔍 Filter items..." />
              <button class="btn btn-sm st-btn-outline" @click="downloadItemsPdf" :disabled="!topItems.length" title="Download as PDF">
                <i class="mdi mdi-file-pdf-box"></i> PDF
              </button>
            </div>
          </div>
          <div v-if="topItems.length" style="font-size:11px;color:#8BA4C4;margin-bottom:6px;">
            Showing <strong style="color:#E8EDF4;">{{ topItems.length }}</strong> item{{ topItems.length === 1 ? '' : 's' }} ·
            Total qty <strong style="color:#10B981;">{{ fmt(topItemsTotalQty, 0) }}</strong> ·
            Total revenue <strong style="color:#10B981;">KSh {{ fmt(topItemsTotalRevenue) }}</strong>
          </div>
          <div v-if="topItems.length" style="overflow-x:auto;">
            <table class="pm-table">
              <thead><tr>
                <th>#</th><th>Item</th><th class="text-end">Qty Sold</th><th class="text-end">Revenue</th><th>Share</th>
              </tr></thead>
              <tbody>
                <tr v-for="(it, i) in topItems" :key="it.name">
                  <td style="color:#4A6A8A;">{{ i + 1 }}</td>
                  <td style="color:#E8EDF4;font-weight:600;">{{ it.name }}</td>
                  <td class="text-end">{{ fmt(it.qty, 0) }}</td>
                  <td class="text-end" style="color:#10B981;font-weight:600;">{{ fmt(it.revenue) }}</td>
                  <td style="min-width:120px;">
                    <div class="prog-track">
                      <div class="prog-fill" :style="{width: pct(itemShare(it), 1) + '%', background: '#10B981'}"></div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="st-empty" style="text-align:center;padding:24px;color:#4A6A8A;font-size:13px;">
            No POS items sold in this range.
          </div>
        </div>

        <!-- TIME TAB -->
        <div v-else-if="posTab === 'time'">
          <div style="font-size:11px;color:#4A6A8A;text-transform:uppercase;font-weight:600;margin-bottom:6px;">Sales by Hour of Day</div>
          <div class="hour-chart">
            <div v-for="h in hourSeries" :key="h.hr" class="hour-bar-wrap" :title="`${h.hr}:00 — KSh ${fmt(h.total)} (${h.cnt} txns)`">
              <div class="hour-bar" :style="{height: barHeight(h.total, hourMax) + 'px', background: h.total === hourMax ? '#10B981' : '#2E86DE'}"></div>
              <div class="hour-lbl">{{ h.hr }}</div>
            </div>
          </div>

          <div style="font-size:11px;color:#4A6A8A;text-transform:uppercase;font-weight:600;margin:18px 0 6px;">Sales by Day of Week</div>
          <table class="pm-table">
            <thead><tr><th>Day</th><th class="text-end">Txns</th><th class="text-end">Total</th><th>Share</th></tr></thead>
            <tbody>
              <tr v-for="d in dowSeries" :key="d.dow">
                <td style="font-weight:600;color:#E8EDF4;">{{ dowLabel(d.dow) }}</td>
                <td class="text-end">{{ d.cnt }}</td>
                <td class="text-end" style="color:#10B981;font-weight:600;">{{ fmt(d.total) }}</td>
                <td style="min-width:120px;">
                  <div class="prog-track">
                    <div class="prog-fill" :style="{width: pct(d.total, dowTotal) + '%', background: '#4FC3F7'}"></div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="report.pos.by_day.length" style="font-size:11px;color:#4A6A8A;text-transform:uppercase;font-weight:600;margin:18px 0 6px;">Daily POS Sales Trend</div>
          <div v-if="report.pos.by_day.length" class="day-chart">
            <div v-for="d in report.pos.by_day" :key="d.d" class="day-bar-wrap" :title="`${d.d} — KSh ${fmt(d.total)} (${d.cnt} txns)`">
              <div class="day-bar" :style="{height: barHeight(d.total, dayMax) + 'px', background: d.total === dayMax ? '#10B981' : '#4FC3F7'}"></div>
            </div>
          </div>
          <div v-if="report.pos.by_day.length" class="d-flex justify-content-between mt-1" style="font-size:10px;color:#4A6A8A;">
            <span>{{ report.pos.by_day[0].d }}</span>
            <span>{{ report.pos.by_day[report.pos.by_day.length - 1].d }}</span>
          </div>
        </div>

        <!-- CASHIER TAB -->
        <div v-else-if="posTab === 'cashier'">
          <div v-if="report.pos.by_cashier.length" style="overflow-x:auto;">
            <table class="pm-table">
              <thead><tr>
                <th>#</th><th>Cashier</th><th class="text-end">Transactions</th><th class="text-end">Sales</th><th class="text-end">Avg Ticket</th><th>Share</th>
              </tr></thead>
              <tbody>
                <tr v-for="(c, i) in report.pos.by_cashier" :key="c.cashier_user_id">
                  <td style="color:#4A6A8A;">{{ i + 1 }}</td>
                  <td style="color:#E8EDF4;font-weight:600;">{{ c.cashier }}</td>
                  <td class="text-end">{{ c.cnt }}</td>
                  <td class="text-end" style="color:#10B981;font-weight:600;">{{ fmt(c.total) }}</td>
                  <td class="text-end" style="color:#8BA4C4;">{{ fmt(Number(c.total) / Math.max(1, Number(c.cnt))) }}</td>
                  <td style="min-width:120px;">
                    <div class="prog-track">
                      <div class="prog-fill" :style="{width: pct(c.total, report.pos.total) + '%', background: '#F59E0B'}"></div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="st-empty" style="text-align:center;padding:24px;color:#4A6A8A;font-size:13px;">
            No cashier data in this range.
          </div>
        </div>
      </div>

      <!-- Outflow log + add form -->
      <div class="st-section mb-3" style="padding:14px;">
        <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <span style="font-size:13px;font-weight:600;color:#4FC3F7;">Outflow Log</span>
          <button class="btn st-btn-primary btn-sm" @click="showAddForm = !showAddForm">
            {{ showAddForm ? '✕ Cancel' : '+ Record outflow' }}
          </button>
        </div>

        <div v-if="showAddForm" class="st-info-box mb-3" style="padding:12px;">
          <div class="d-flex gap-2 flex-wrap">
            <div style="min-width:140px;">
              <label class="st-label" style="font-size:10px;">Type</label>
              <select class="st-input" v-model="form.outflow_type" style="padding:9px 10px;">
                <option value="remittance">Remittance (B2C)</option>
                <option value="expense">Direct Till Expense</option>
              </select>
            </div>
            <div style="min-width:150px;">
              <label class="st-label" style="font-size:10px;">Paid On</label>
              <input type="date" class="st-input" v-model="form.paid_on" />
            </div>
            <div style="min-width:130px;">
              <label class="st-label" style="font-size:10px;">Amount (KES)</label>
              <input type="number" step="0.01" min="0" class="st-input" v-model="form.amount" placeholder="0.00" />
            </div>
            <div style="min-width:160px;">
              <label class="st-label" style="font-size:10px;">M-Pesa Code (optional)</label>
              <input class="st-input" v-model="form.mpesa_code" placeholder="e.g. UE5NA3BDHB" style="text-transform:uppercase;" />
            </div>
            <div style="min-width:180px;flex:1;">
              <label class="st-label" style="font-size:10px;">Recipient / Payee</label>
              <input class="st-input" v-model="form.recipient_name" placeholder="e.g. Nancy Chebet, WASO 1" />
            </div>
            <div style="min-width:220px;flex:2;">
              <label class="st-label" style="font-size:10px;">Description (optional)</label>
              <input class="st-input" v-model="form.description" placeholder="Notes" />
            </div>
          </div>
          <div class="d-flex gap-2 mt-3 align-items-center">
            <button class="btn st-btn-primary btn-sm" @click="submitOutflow" :disabled="submitting || !canSubmit">
              <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>Save
            </button>
            <button class="btn st-btn-outline btn-sm" @click="resetForm">Reset</button>
            <span v-if="formMsg" :style="{color: formOk ? '#10B981' : '#EF4444',fontSize:'12px'}">{{ formMsg }}</span>
          </div>
        </div>

        <div v-if="report && report.log.length" style="overflow-x:auto;">
          <table class="pm-table">
            <thead><tr>
              <th>Date</th><th>Type</th><th>Recipient</th><th>Description</th><th>M-Pesa</th>
              <th class="text-end">Amount</th><th>By</th><th></th>
            </tr></thead>
            <tbody>
              <tr v-for="row in report.log" :key="row.id">
                <td>{{ row.paid_on }}</td>
                <td>
                  <span class="pm-badge" :class="row.outflow_type === 'remittance' ? 'pm-badge-info' : 'pm-badge-warn'">
                    {{ row.outflow_type === 'remittance' ? 'Remittance' : 'Expense' }}
                  </span>
                </td>
                <td>{{ row.recipient_name || '—' }}</td>
                <td style="max-width:240px;white-space:normal;color:#8BA4C4;font-size:11px;">{{ row.description || '' }}</td>
                <td style="font-family:monospace;font-size:11px;color:#8BA4C4;">{{ row.mpesa_code || '—' }}</td>
                <td class="text-end" :style="{color: row.outflow_type === 'remittance' ? '#4FC3F7' : '#F59E0B', fontWeight:600}">{{ fmt(row.amount) }}</td>
                <td style="color:#8BA4C4;font-size:11px;">{{ row.recorded_by_name || '—' }}</td>
                <td>
                  <button class="btn btn-sm" style="padding:2px 8px;font-size:11px;background:rgba(239,68,68,.12);color:#EF4444;border:1px solid rgba(239,68,68,.4);border-radius:6px;"
                          @click="deleteOutflow(row)" :disabled="deletingId === row.id">
                    <span v-if="deletingId === row.id" class="spinner-border spinner-border-sm me-1"></span>🗑
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else-if="report" class="st-empty" style="text-align:center;padding:24px;color:#4A6A8A;font-size:13px;">
          No outflows recorded in this range. Click "+ Record outflow" to add one.
        </div>
      </div>

      <div v-if="loading && !report" class="st-spinner"><div class="spinner-border"></div></div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: 'SalesReport',
  components: { Navigation },
  data() {
    const today = new Date().toISOString().slice(0, 10);
    const firstOfMonth = today.slice(0, 8) + '01';
    return {
      dateFrom: firstOfMonth,
      dateTo: today,
      report: null,
      loading: false,
      showAddForm: false,
      submitting: false,
      formMsg: '', formOk: false,
      deletingId: null,
      form: this.makeEmptyForm(today),
      posTab: 'items',
      itemSort: 'revenue',
      itemFilter: '',
      itemMinQty: 0,
    };
  },
  computed: {
    canSubmit() {
      return this.form.outflow_type && this.form.paid_on && Number(this.form.amount) > 0;
    },
    topItems() {
      if (!this.report?.pos) return [];
      const src = this.itemSort === 'qty' ? this.report.pos.top_by_qty : this.report.pos.top_by_revenue;
      const q = this.itemFilter.trim().toLowerCase();
      const min = Number(this.itemMinQty) || 0;
      return src.filter(it => {
        if (q && !(it.name || '').toLowerCase().includes(q)) return false;
        if (min > 0 && Number(it.qty || 0) < min) return false;
        return true;
      });
    },
    topItemsTotalQty() { return this.topItems.reduce((s, it) => s + Number(it.qty || 0), 0); },
    topItemsTotalRevenue() { return this.topItems.reduce((s, it) => s + Number(it.revenue || 0), 0); },
    hourSeries() {
      if (!this.report?.pos) return [];
      const map = {};
      (this.report.pos.by_hour || []).forEach(h => { map[Number(h.hr)] = { hr: Number(h.hr), cnt: Number(h.cnt), total: Number(h.total) }; });
      const out = [];
      for (let h = 0; h < 24; h++) out.push(map[h] || { hr: h, cnt: 0, total: 0 });
      return out;
    },
    hourMax() {
      return Math.max(1, ...this.hourSeries.map(h => h.total));
    },
    bestHour() {
      if (!this.report?.pos) return { label: '—', total: 0 };
      const best = this.hourSeries.reduce((a, b) => (b.total > a.total ? b : a), { hr: 0, total: 0 });
      if (best.total === 0) return { label: '—', total: 0 };
      const pad = n => String(n).padStart(2, '0');
      return { label: `${pad(best.hr)}:00`, total: best.total };
    },
    dowSeries() {
      // dow: 1=Sunday … 7=Saturday in MySQL
      if (!this.report?.pos) return [];
      const order = [2, 3, 4, 5, 6, 7, 1]; // Mon → Sun
      const map = {};
      (this.report.pos.by_dow || []).forEach(d => { map[Number(d.dow)] = { dow: Number(d.dow), cnt: Number(d.cnt), total: Number(d.total) }; });
      return order.map(dw => map[dw] || { dow: dw, cnt: 0, total: 0 });
    },
    dowTotal() {
      return this.dowSeries.reduce((s, d) => s + Number(d.total), 0) || 1;
    },
    dayMax() {
      if (!this.report?.pos) return 1;
      return Math.max(1, ...(this.report.pos.by_day || []).map(d => Number(d.total)));
    },
  },
  mounted() { this.loadReport(); },
  methods: {
    fmt(n, decimals = 2) { return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }); },
    methodLabel(m) { return { mpesa_stk: 'M-Pesa STK', mpesa_c2b: 'M-Pesa C2B', mpesa_paybill: 'Paybill', mpesa_till: 'Till', bank_transfer: 'Bank', card: 'Card', cash: 'Cash', cheque: 'Cheque' }[m] || m; },
    modeLabel(m) { return { wallet_tap: 'Wallet', mpesa: 'M-Pesa', cash: 'Cash', card: 'Card' }[m] || m; },
    modeColor(m) { return { wallet_tap: '#A855F7', mpesa: '#10B981', cash: '#F59E0B', card: '#4FC3F7' }[m] || '#8BA4C4'; },
    dowLabel(dw) { return ['', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dw] || ''; },
    pct(part, whole) { const w = Number(whole) || 0; if (w === 0) return 0; return Math.round((Number(part) / w) * 1000) / 10; },
    itemShare(it) { if (!this.report?.pos?.total) return 0; return Number(it.revenue) / Number(this.report.pos.total); },
    downloadItemsPdf() {
      const inst = sessionStorage.getItem('institutionName') || 'Simitrac';
      const from = this.dateFrom || '';
      const to = this.dateTo || '';
      const sortLabel = this.itemSort === 'qty' ? 'Quantity' : 'Revenue';
      const minQtyNote = this.itemMinQty > 0 ? ` · Min qty ≥ ${this.itemMinQty}` : '';
      const filterNote = this.itemFilter ? ` · Filter "${this.itemFilter}"` : '';
      const rows = this.topItems.map((it, i) => `
        <tr>
          <td>${i + 1}</td>
          <td>${this.escHtml(it.name)}</td>
          <td style="text-align:right;">${this.fmt(it.qty, 0)}</td>
          <td style="text-align:right;color:#059669;">KSh ${this.fmt(it.revenue)}</td>
          <td style="text-align:right;">${this.pct(this.itemShare(it), 1)}%</td>
        </tr>`).join('');
      const html = `<!doctype html><html><head><meta charset="utf-8">
        <title>Sales by Item — ${this.escHtml(inst)}</title>
        <style>
          body{font-family:Arial,sans-serif;color:#1f2937;padding:24px;margin:0;}
          h1{font-size:20px;margin:0 0 4px;}
          .meta{font-size:12px;color:#555;margin-bottom:14px;}
          .summary{background:#f3f4f6;border-radius:8px;padding:10px 14px;font-size:12px;margin-bottom:14px;display:flex;gap:18px;flex-wrap:wrap;}
          .summary b{color:#059669;}
          table{width:100%;border-collapse:collapse;font-size:12px;}
          th{background:#0F2347;color:#fff;text-align:left;padding:8px;font-weight:600;}
          th:nth-child(3),th:nth-child(4),th:nth-child(5){text-align:right;}
          td{padding:7px 8px;border-bottom:1px solid #e5e7eb;}
          tr:nth-child(even) td{background:#fafafa;}
          .footer{margin-top:20px;font-size:10px;color:#888;text-align:center;border-top:1px solid #e5e7eb;padding-top:10px;}
          @media print{ .noprint{display:none;} }
        </style></head><body>
        <h1>📊 Sales by Item</h1>
        <div class="meta">
          <strong>${this.escHtml(inst)}</strong><br>
          Period: <strong>${from} → ${to}</strong> · Sorted by <strong>${sortLabel}</strong>${minQtyNote}${filterNote}<br>
          Generated: ${new Date().toLocaleString()}
        </div>
        <div class="summary">
          <div>Items: <b>${this.topItems.length}</b></div>
          <div>Total Quantity: <b>${this.fmt(this.topItemsTotalQty, 0)}</b></div>
          <div>Total Revenue: <b>KSh ${this.fmt(this.topItemsTotalRevenue)}</b></div>
        </div>
        <table>
          <thead><tr><th>#</th><th>Item</th><th>Qty Sold</th><th>Revenue</th><th>Share</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="footer">Simitrac — Sales by Item · simitrac.co.ke</div>
        <div class="noprint" style="margin-top:14px;text-align:center;">
          <button onclick="window.print()" style="background:#0F2347;color:#fff;border:none;padding:10px 22px;border-radius:8px;font-weight:600;cursor:pointer;">🖨 Print / Save as PDF</button>
        </div>
      </body></html>`;
      const w = window.open('', '_blank');
      if (!w) { alert('Pop-up blocked — allow pop-ups for simitrac.co.ke and try again.'); return; }
      w.document.write(html);
      w.document.close();
      setTimeout(() => { try { w.focus(); w.print(); } catch (e) {} }, 400);
    },
    escHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); },
    barHeight(v, max) { const m = Number(max) || 1; return Math.max(2, Math.round((Number(v) / m) * 110)); },
    makeEmptyForm(today) {
      return { outflow_type: 'remittance', paid_on: today, amount: '', mpesa_code: '', recipient_name: '', description: '' };
    },
    resetForm() {
      this.form = this.makeEmptyForm(new Date().toISOString().slice(0, 10));
      this.formMsg = '';
    },
    setRange(kind) {
      const today = new Date();
      const isoToday = today.toISOString().slice(0, 10);
      if (kind === 'today') { this.dateFrom = isoToday; this.dateTo = isoToday; }
      else if (kind === 'week') {
        const d = new Date(); d.setDate(d.getDate() - 6);
        this.dateFrom = d.toISOString().slice(0, 10); this.dateTo = isoToday;
      } else if (kind === 'mtd') {
        this.dateFrom = isoToday.slice(0, 8) + '01'; this.dateTo = isoToday;
      } else if (kind === '30d') {
        const d = new Date(); d.setDate(d.getDate() - 29);
        this.dateFrom = d.toISOString().slice(0, 10); this.dateTo = isoToday;
      }
      this.loadReport();
    },
    async loadReport() {
      this.loading = true;
      try {
        const res = await executeJson({ function: 'getSalesReport', dateFrom: this.dateFrom, dateTo: this.dateTo }, simitrack);
        if (res.data.success) {
          this.report = res.data.data;
        } else {
          alert(res.data.message || 'Failed to load report');
        }
      } catch (e) {
        alert('Error: ' + (e.response?.data?.message || e.message));
      }
      this.loading = false;
    },
    async submitOutflow() {
      this.submitting = true;
      this.formMsg = '';
      try {
        const payload = { function: 'addTillOutflow', ...this.form };
        if (payload.mpesa_code) payload.mpesa_code = String(payload.mpesa_code).trim().toUpperCase();
        const res = await executeJson(payload, simitrack);
        if (res.data.success) {
          this.formOk = true;
          this.formMsg = '✓ Recorded';
          this.resetForm();
          this.showAddForm = false;
          this.loadReport();
        } else {
          this.formOk = false;
          this.formMsg = res.data.message || 'Failed';
        }
      } catch (e) {
        this.formOk = false;
        this.formMsg = e.response?.data?.message || e.message;
      }
      this.submitting = false;
    },
    async deleteOutflow(row) {
      if (!confirm(`Delete this ${row.outflow_type} of KSh ${this.fmt(row.amount)}?`)) return;
      this.deletingId = row.id;
      try {
        const res = await executeJson({ function: 'deleteTillOutflow', id: row.id }, simitrack);
        if (res.data.success) {
          this.loadReport();
        } else {
          alert(res.data.message || 'Failed to delete');
        }
      } catch (e) {
        alert('Error: ' + (e.response?.data?.message || e.message));
      }
      this.deletingId = null;
    },
    exportCsv() {
      if (!this.report) return;
      const lines = [];
      lines.push(`Sales Report,${this.report.date_from} to ${this.report.date_to}`);
      lines.push('');
      lines.push('Summary');
      lines.push(`Collected,${this.report.collections.total}`);
      lines.push(`Remitted,${this.report.remittance.total}`);
      lines.push(`Till Expenses,${this.report.expense.total}`);
      lines.push(`Float,${this.report.float}`);
      lines.push('');
      lines.push('Collections by Method');
      lines.push('Method,Count,Total');
      (this.report.by_method || []).forEach(m => lines.push(`${m.payment_method},${m.cnt},${m.total}`));
      lines.push('');
      lines.push('Outflows');
      lines.push('Date,Type,Recipient,Description,M-Pesa,Amount,Recorded By');
      (this.report.log || []).forEach(r => {
        const safe = v => `"${String(v || '').replace(/"/g, '""')}"`;
        lines.push([r.paid_on, r.outflow_type, safe(r.recipient_name), safe(r.description), r.mpesa_code || '', r.amount, safe(r.recorded_by_name)].join(','));
      });
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales-report-${this.report.date_from}-to-${this.report.date_to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-section{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;}
.st-label{color:#8BA4C4;font-size:12px;font-weight:600;margin-bottom:4px;display:block;}
.st-input{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:8px 12px;color:#E8EDF4;font-size:13px;outline:none;}.st-input:focus{border-color:#2E86DE;}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}
.st-btn-primary:disabled{opacity:.6;}
.st-btn-outline{background:transparent;border:1px solid #1A3A6A;color:#8BA4C4;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}.st-btn-outline:hover{border-color:#4FC3F7;color:#4FC3F7;}
.st-info-box{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;}
.st-spinner{display:flex;justify-content:center;padding:30px;}.st-spinner .spinner-border{color:#2E86DE;}

.pm-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:10px;margin-bottom:16px;}
.pm-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:10px;padding:14px;border-left:3px solid #1A3A6A;}
.pm-card-label{font-size:10px;color:#4A6A8A;text-transform:uppercase;font-weight:600;letter-spacing:.5px;}
.pm-card-value{font-size:20px;font-weight:800;margin:2px 0;}
.pm-card-sub{font-size:11px;color:#8BA4C4;}

.pm-badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:600;}
.pm-badge-warn{background:rgba(245,158,11,.15);color:#F59E0B;}
.pm-badge-info{background:rgba(79,195,247,.15);color:#4FC3F7;}

.pm-table{width:100%;border-collapse:collapse;font-size:12px;color:#E8EDF4;}
.pm-table thead tr{background:#0A1628;}
.pm-table th{padding:8px;color:#4FC3F7;font-weight:600;text-align:left;font-size:10px;text-transform:uppercase;border-bottom:2px solid #1A3A6A;white-space:nowrap;}
.pm-table td{padding:7px 8px;border-bottom:1px solid #1A3A6A;}
.pm-table tr:hover{background:rgba(46,134,222,.05);}
.text-end{text-align:right;}

.recon-row{display:flex;justify-content:space-between;align-items:center;padding:6px 0;border-bottom:1px solid #1A3A6A;color:#E8EDF4;}
.recon-row:last-child{border-bottom:none;}
.recon-total{padding-top:10px;margin-top:6px;border-top:2px solid #1A3A6A;border-bottom:none;font-size:14px;}

/* Payment-mode stacked bar */
.mode-bar{display:flex;width:100%;height:24px;border-radius:6px;overflow:hidden;background:#0A1628;border:1px solid #1A3A6A;}
.mode-bar-seg{display:flex;align-items:center;justify-content:center;color:#0A1628;font-size:10px;font-weight:700;white-space:nowrap;transition:flex .2s;}

/* Hour-of-day chart */
.hour-chart{display:grid;grid-template-columns:repeat(24,1fr);align-items:end;gap:2px;height:130px;padding:6px 0;border-bottom:1px solid #1A3A6A;}
.hour-bar-wrap{display:flex;flex-direction:column;align-items:center;gap:2px;height:100%;justify-content:flex-end;cursor:default;}
.hour-bar{width:100%;min-height:2px;border-radius:3px 3px 0 0;background:#2E86DE;transition:background .2s;}
.hour-bar-wrap:hover .hour-bar{filter:brightness(1.2);}
.hour-lbl{font-size:9px;color:#4A6A8A;}

/* Daily POS bar chart */
.day-chart{display:flex;align-items:end;gap:2px;height:80px;padding:6px 0;border-bottom:1px solid #1A3A6A;}
.day-bar-wrap{flex:1;min-width:6px;display:flex;align-items:end;justify-content:center;height:100%;}
.day-bar{width:100%;min-height:2px;border-radius:2px 2px 0 0;}

/* Progress bars (share columns) */
.prog-track{width:100%;height:6px;background:#0A1628;border:1px solid #1A3A6A;border-radius:3px;overflow:hidden;}
.prog-fill{height:100%;border-radius:3px;transition:width .3s;}

.st-empty{color:#4A6A8A;}
</style>
